import React, { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

/**
 * BranchSelector — renders a required branch dropdown when the tenant has
 * multi_branch_enabled. Returns null when multi-branch is off so pages don't
 * need to conditionally include it themselves.
 *
 * Auto-default behaviour (v3.8):
 *   • If the tenant has exactly ONE active branch, that branch is
 *     auto-selected (no user action needed).
 *   • If more than one branch exists, the user must explicitly pick one.
 *     Transaction dialogs can wrap their submit handler in
 *     `confirmBranchSelection(branchId, tenant, branches)` (below) to show
 *     a confirmation modal — reduces the risk of posting to the wrong
 *     branch on multi-branch tenants.
 *
 * Props:
 *   value       - selected branch_id (string | "")
 *   onChange    - (branch_id) => void
 *   label       - optional label override (default "Branch")
 *   testId      - data-testid for the trigger
 *   allowAll    - if true, adds "All Branches" option (for filters/reports)
 *   className   - wrapper class
 *   autoDefault - if true (default), auto-selects the sole branch when
 *                 only one exists.
 */
export default function BranchSelector({
  value = "",
  onChange,
  label = "Branch",
  testId = "branch-select",
  allowAll = false,
  className = "",
  autoDefault = true,
}) {
  const { tenant } = useAuth();
  const enabled = !!tenant?.multi_branch_enabled;
  const branches = (tenant?.branches || []).filter((b) => b.active !== false);

  // Auto-default single branch — MUST run before the null return so the
  // parent form receives the correct branch_id even though the dropdown
  // does not render.
  useEffect(() => {
    if (!enabled || allowAll) return;
    if (autoDefault && branches.length === 1 && !value) {
      onChange && onChange(branches[0].branch_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, branches.length, autoDefault, allowAll, value]);

  if (!enabled) return null;
  // Only-one-branch: no need to show the dropdown; the field is already
  // set via the useEffect above.
  if (!allowAll && branches.length === 1) return null;

  if (!allowAll && branches.length === 0) {
    return (
      <div className={className}>
        <Label className="text-xs font-mono uppercase tracking-wider">{label}</Label>
        <div className="text-[11px] text-amber-600 mt-1 font-mono">
          No active branches — add one under Settings → Branches.
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <Label className="text-xs font-mono uppercase tracking-wider">
        {label}{!allowAll && <span className="text-rose-500 ml-0.5">*</span>}
      </Label>
      <Select value={value || (allowAll ? "__all__" : "")} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger data-testid={testId}>
          <SelectValue placeholder={allowAll ? "All Branches" : "Select branch"} />
        </SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="__all__">All Branches</SelectItem>}
          {branches.map((b) => (
            <SelectItem key={b.branch_id} value={b.branch_id}>
              {b.code} · {b.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * confirmBranchSelection — helper for transaction dialogs on multi-branch
 * tenants. When there are 2+ branches, shows a native confirm() modal to
 * ensure the user is posting to the right branch. Auto-passes on single-
 * branch tenants and non-multi-branch tenants.
 *
 * Usage in dialog submit:
 *   if (!confirmBranchSelection(form.branch_id, tenant)) return;
 *   ...continue POST...
 *
 * Returns true if OK to proceed, false if user cancelled or branch missing.
 */
export function confirmBranchSelection(branchId, tenant) {
  if (!tenant?.multi_branch_enabled) return true;
  const branches = (tenant?.branches || []).filter((b) => b.active !== false);
  if (branches.length <= 1) return true;
  if (!branchId) {
    // eslint-disable-next-line no-alert
    alert("Please select a branch before posting this transaction.");
    return false;
  }
  const br = branches.find((b) => b.branch_id === branchId);
  const name = br ? `${br.code} · ${br.name}` : branchId;
  // eslint-disable-next-line no-alert
  return window.confirm(`Post this transaction to branch:\n\n${name}\n\nContinue?`);
}

/** Convenience hook for pages that just need the branch list + enabled flag. */
export function useBranches() {
  const { tenant } = useAuth();
  return {
    enabled: !!tenant?.multi_branch_enabled,
    branches: (tenant?.branches || []).filter((b) => b.active !== false),
    all: tenant?.branches || [],
  };
}
